/**
 * Server-sent messages, rendered in the guest's language.
 *
 * The API answers in English and it is the only party that knows what actually
 * went wrong — a stop-listed dish, a dine-in session that ran out, a rate limit.
 * Teaching the backend about languages would make every error message a
 * translation surface and put the storefront's vocabulary on the server, so
 * instead each message is matched back to the *situation* it describes and the
 * catalog supplies the words.
 *
 * Anything unrecognised falls through to the server's own sentence. That is the
 * deliberate trade: an English sentence that says what happened beats a
 * localised one that does not, and a new backend message shows up as untranslated
 * text rather than as a wrong explanation.
 */

const detailOf = (error) => {
  const detail = error?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  return "";
};

const has = (haystack, needle) => haystack.toLowerCase().includes(needle);

/** Network failure: `fetch` rejected, so there is no status and no detail. */
const isOffline = (error) => error?.status === undefined || error?.status === null;

/**
 * Why a scanned QR code was refused.
 *
 * The distinction the backend draws is worth keeping: a code that belongs to
 * another restaurant, one that has been reprinted, and one that has simply aged
 * out send a guest to three different actions.
 */
export function qrErrorMessage(t, error) {
  const detail = detailOf(error);
  if (has(detail, "another restaurant")) return t("errors.qr.otherRestaurant");
  if (has(detail, "been replaced")) return t("errors.qr.replaced");
  if (has(detail, "expired")) return t("errors.qr.expired");
  if (has(detail, "not valid")) return t("errors.qr.invalid");
  if (has(detail, "scan the code")) return t("errors.scan.expired");
  if (has(detail, "scan the qr code")) return t("errors.scan.required");
  return detail || t("errors.qr.fallback");
}

/** The scan gate's own explanation, shown instead of the ordering screens. */
export function scanErrorMessage(t, error) {
  const detail = detailOf(error);
  if (!detail) return null;
  return qrErrorMessage(t, error);
}

/** POST /api/orders — the one failure a guest must not be left guessing about. */
export function orderErrorMessage(t, error) {
  const detail = detailOf(error);
  const status = error?.status;

  if (isOffline(error)) return t("errors.network");
  if (status === 429) return t("errors.rateLimited");
  if (status === 401) return t("errors.scan.expired");
  if (status === 409 && has(detail, "no longer available")) {
    const dish = detail.replace(/\s*is no longer available\.?$/i, "").trim();
    if (dish) return t("errors.order.unavailable", { dish });
  }
  if (status === 422 && has(detail, "unknown dish")) return t("errors.order.unknownDish");
  if (status >= 500) return t("errors.server");
  return detail || t("errors.order.generic");
}

/** POST /api/auto-plate. `ai_empty` is ours: the call succeeded with no plates. */
export function aiErrorMessage(t, error) {
  const detail = detailOf(error);
  const status = error?.status;

  if (error?.code === "ai_empty") return t("errors.ai.empty");
  if (isOffline(error)) return t("errors.network");
  if (status === 429) return t("errors.rateLimited");
  if (status === 404 && has(detail, "not enabled")) return t("errors.ai.notEnabled");
  if (status === 503 && has(detail, "menu is empty")) return t("errors.ai.menuEmpty");
  if (status === 502 || status >= 500) return t("errors.ai.failed");
  return detail || t("errors.ai.failed");
}

/** Loading the restaurant and its menu. */
export function tenantErrorMessage(t, error) {
  if (isOffline(error)) return t("errors.network");
  if (error?.status === 429) return t("errors.rateLimited");
  if (error?.status >= 500) return t("errors.server");
  return detailOf(error) || t("errors.network");
}

/**
 * PUT /api/profile and friends.
 *
 * A validation message from the server names the field that was wrong, which is
 * the whole value of it, so it wins over the generic line.
 */
export function saveErrorMessage(t, error) {
  const detail = detailOf(error);
  if (isOffline(error)) return t("errors.network");
  if (error?.status === 429) return t("errors.rateLimited");
  return detail || t("errors.saveFailed");
}
